import { useState } from 'react';
import type { BureauConfigDTO } from '@pointage/shared';
import { toast } from '../../store/toast.js';
import { useBureauConfigMutation } from '../../api/bureau.js';

export function ReglagesView({ config }: { config: BureauConfigDTO }) {
  const mut = useBureauConfigMutation();
  const run = (method: 'post' | 'patch' | 'put', path: string, body: unknown, ok?: string) =>
    mut.mutate({ method, path, body }, {
      onSuccess: () => { if (ok) toast.success(ok); },
      onError: () => toast.danger('Le réglage n’a pas pu être enregistré.'),
    });

  const togglePerson = (key: 'assigneeIds' | 'escalateToIds', id: string) => {
    const cur = new Set(config[key]);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    run('put', '/people', { assigneeIds: config.assigneeIds, escalateToIds: config.escalateToIds, [key]: [...cur] }, 'Enregistré');
  };

  return (
    <div className="bx-settings">
      <div className="card">
        <div className="card-header"><div className="card-title">Personnes</div></div>
        <div className="bx-set-body">
          <p className="bx-hint">
            La <b>responsable</b> reçoit la journée et ses rappels. Les <b>référents</b> reçoivent les escalades, les
            signalements et les validations. Seuls les comptes qui ont accès au Bureau apparaissent ici : pour Lamis,
            accorde d’abord la permission « Bureau » dans Paramètres → Accès.
          </p>
          <table className="bx-set-table">
            <thead><tr><th scope="col">Compte</th><th scope="col">Responsable</th><th scope="col">Référent</th></tr></thead>
            <tbody>
              {config.people.map((p) => (
                <tr key={p.id}>
                  <td><b>{p.name}</b> <span className="bx-follow-meta">{p.email}</span></td>
                  <td><input type="checkbox" aria-label={`${p.name} responsable`} checked={config.assigneeIds.includes(p.id)} onChange={() => togglePerson('assigneeIds', p.id)} /></td>
                  <td><input type="checkbox" aria-label={`${p.name} référent`} disabled={!p.canManage} checked={config.escalateToIds.includes(p.id)} onChange={() => togglePerson('escalateToIds', p.id)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">Moments de la journée</div><span className="bx-follow-meta">Dim. → jeu., jours fériés exclus</span></div>
        {config.moments.map((m) => <MomentEditor key={m.id} m={m} run={run} />)}
        <AddMoment run={run} />
      </div>

      <div className="bx-set-grid">
        <div className="card">
          <div className="card-header"><div className="card-title">Factures récurrentes</div></div>
          <div className="bx-set-body">
            <p className="bx-hint">Un suivi « à ne jamais oublier » s’ouvre 10 jours avant chaque échéance. Sans jour renseigné, rien n’est généré.</p>
            {config.bills.map((b) => (
              <div className="bx-set-row" key={b.id}>
                <span className="bx-set-label">{b.label}</span>
                <label className="bx-follow-meta" htmlFor={`bill-${b.id}`}>Échéance le</label>
                <select
                  id={`bill-${b.id}`} className="input bx-day" value={b.dayOfMonth ?? ''}
                  onChange={(e) => run('patch', `/bills/${b.id}`, { dayOfMonth: e.target.value ? Number(e.target.value) : null }, 'Échéance enregistrée')}
                >
                  <option value="">— jour —</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <span className="bx-follow-meta">du mois</span>
                <label className="bx-switch"><input type="checkbox" checked={b.active} onChange={() => run('patch', `/bills/${b.id}`, { active: !b.active })} /> active</label>
              </div>
            ))}
            <AddInline placeholder="Autre facture (électricité…)" onAdd={(label) => run('post', '/bills', { label, dayOfMonth: null }, 'Facture ajoutée')} />
          </div>
        </div>

        <div className="card">
          <div className="card-header"><div className="card-title">Articles de stock</div></div>
          <div className="bx-set-body">
            {config.stock.map((s) => (
              <div className="bx-set-row" key={s.id}>
                <span className="bx-follow-meta bx-cat">{s.category}</span>
                <span className={s.active ? 'bx-set-label' : 'bx-set-label is-off'}>{s.label}</span>
                <label className="bx-switch"><input type="checkbox" checked={s.active} onChange={() => run('patch', `/stock/${s.id}`, { active: !s.active })} /> suivi</label>
              </div>
            ))}
            <AddStock run={run} categories={[...new Set(config.stock.map((s) => s.category))]} />
          </div>
        </div>
      </div>
    </div>
  );
}

type Run = (method: 'post' | 'patch' | 'put', path: string, body: unknown, ok?: string) => void;

function MomentEditor({ m, run }: { m: BureauConfigDTO['moments'][number]; run: Run }) {
  const [time, setTime] = useState(m.time);
  return (
    <div className={m.active ? 'bx-set-moment' : 'bx-set-moment is-off'}>
      <div className="bx-set-row">
        <label className="sr-only" htmlFor={`mt-${m.id}`}>Heure</label>
        <input id={`mt-${m.id}`} type="time" className="input bx-time" value={time} onChange={(e) => setTime(e.target.value)} onBlur={() => { if (time && time !== m.time) run('patch', `/moments/${m.id}`, { time }, 'Heure enregistrée'); }} />
        <b className="bx-set-label">{m.label}</b>
        <label className="bx-switch"><input type="checkbox" checked={m.escalate} onChange={() => run('patch', `/moments/${m.id}`, { escalate: !m.escalate })} /> escalade à +45 min</label>
        <label className="bx-switch"><input type="checkbox" checked={m.active} onChange={() => run('patch', `/moments/${m.id}`, { active: !m.active })} /> actif</label>
      </div>
      <ul className="bx-set-items">
        {m.items.map((i) => (
          <li key={i.id} className={i.active ? '' : 'is-off'}>
            <span className="bx-set-label">{i.label}</span>
            {i.autoKey
              ? <span className="bx-pill auto">Auto · Saisie</span>
              : <label className="bx-switch"><input type="checkbox" checked={i.photoRequired} onChange={() => run('patch', `/items/${i.id}`, { photoRequired: !i.photoRequired })} /> photo</label>}
            <label className="bx-switch"><input type="checkbox" checked={i.active} onChange={() => run('patch', `/items/${i.id}`, { active: !i.active })} /> actif</label>
          </li>
        ))}
      </ul>
      <AddInline placeholder="Ajouter un point de contrôle…" onAdd={(label) => run('post', '/items', { momentId: m.id, label, photoRequired: false }, 'Point ajouté')} />
    </div>
  );
}

function AddMoment({ run }: { run: Run }) {
  const [label, setLabel] = useState('');
  const [time, setTime] = useState('12:00');
  return (
    <form className="bx-set-row bx-add" onSubmit={(e) => { e.preventDefault(); if (label.trim()) { run('post', '/moments', { label: label.trim(), time }, 'Moment ajouté'); setLabel(''); } }}>
      <label className="sr-only" htmlFor="new-moment-time">Heure</label>
      <input id="new-moment-time" type="time" className="input bx-time" value={time} onChange={(e) => setTime(e.target.value)} />
      <label className="sr-only" htmlFor="new-moment-label">Nouveau moment</label>
      <input id="new-moment-label" className="input" placeholder="Nouveau moment (ex. Contrôle du studio)" value={label} onChange={(e) => setLabel(e.target.value)} />
      <button type="submit" className="btn btn-sm">Ajouter</button>
    </form>
  );
}

function AddStock({ run, categories }: { run: Run; categories: string[] }) {
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState(categories[0] ?? 'Cuisine');
  return (
    <form className="bx-set-row bx-add" onSubmit={(e) => { e.preventDefault(); if (label.trim()) { run('post', '/stock', { category, label: label.trim() }, 'Article ajouté'); setLabel(''); } }}>
      <label className="sr-only" htmlFor="new-stock-cat">Catégorie</label>
      <input id="new-stock-cat" className="input bx-cat-input" list="bx-cats" value={category} onChange={(e) => setCategory(e.target.value)} />
      <datalist id="bx-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
      <label className="sr-only" htmlFor="new-stock-label">Article</label>
      <input id="new-stock-label" className="input" placeholder="Nouvel article (ex. Thé)" value={label} onChange={(e) => setLabel(e.target.value)} />
      <button type="submit" className="btn btn-sm">Ajouter</button>
    </form>
  );
}

function AddInline({ placeholder, onAdd }: { placeholder: string; onAdd: (v: string) => void }) {
  const [v, setV] = useState('');
  return (
    <form className="bx-set-row bx-add" onSubmit={(e) => { e.preventDefault(); if (v.trim()) { onAdd(v.trim()); setV(''); } }}>
      <label className="sr-only" htmlFor={`add-${placeholder}`}>{placeholder}</label>
      <input id={`add-${placeholder}`} className="input" placeholder={placeholder} value={v} onChange={(e) => setV(e.target.value)} />
      <button type="submit" className="btn btn-sm">Ajouter</button>
    </form>
  );
}
